import React, { useState } from 'react';
import { Plus, Filter, Download } from 'lucide-react';

const Migrations = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const migrations = [
    {
      id: 'VM-20240728-001',
      dbType: 'Vector Embedding',
      source: 'ChromaDB',
      destination: 'Qdrant',
      status: 'Completed',
      timestamp: '2024-07-28 10:30 AM',
      records: '15,000',
      duration: '45 min'
    },
    {
      id: 'VM-20240728-002',
      dbType: 'Vector Index',
      source: 'Pinecone',
      destination: 'Weaviate',
      status: 'In Progress',
      timestamp: '2024-07-28 09:15 AM',
      records: '8,500',
      duration: '30 min',
      progress: 65
    },
    {
      id: 'VM-20240728-003',
      dbType: 'Metadata Sync',
      source: 'ChromaDB',
      destination: 'Pinecone',
      status: 'Pending',
      timestamp: '2024-07-28 08:00 AM',
      records: '12,000',
      duration: '-'
    },
    {
      id: 'VM-20240725-004',
      dbType: 'Vector Embedding',
      source: 'Qdrant',
      destination: 'Weaviate',
      status: 'Failed',
      timestamp: '2024-07-25 04:45 PM',
      records: '0',
      duration: '5 min'
    }
  ];

  const filters = [
    { label: 'All', value: 'all' },
    { label: 'Completed', value: 'completed' },
    { label: 'In Progress', value: 'in-progress' },
    { label: 'Pending', value: 'pending' },
    { label: 'Failed', value: 'failed' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'text-emerald-400 bg-emerald-500/20';
      case 'In Progress':
        return 'text-blue-400 bg-blue-500/20';
      case 'Pending':
        return 'text-amber-400 bg-amber-500/20';
      case 'Failed':
        return 'text-red-400 bg-red-500/20';
      default:
        return 'text-gray-400 bg-gray-500/20';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Migrations</h1>
          <p className="text-gray-400 mt-1">Manage and monitor all your vector database migrations</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">
          <Plus size={20} />
          <span>New Migration</span>
        </button>
      </div>

      {/* Filters and Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-2">
          {filters.map((filter) => (
            <button
              key={filter.value}
              onClick={() => setActiveFilter(filter.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeFilter === filter.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-[#141414] text-gray-400 hover:bg-gray-800 border border-gray-800'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button className="flex items-center gap-2 px-4 py-2 bg-[#141414] text-gray-400 border border-gray-800 rounded-lg hover:bg-gray-800 transition-colors">
            <Filter size={18} />
            <span>Filter</span>
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-[#141414] text-gray-400 border border-gray-800 rounded-lg hover:bg-gray-800 transition-colors">
            <Download size={18} />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Migrations Table */}
      <div className="bg-[#141414] border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800/50">
              <tr>
                <th className="text-left py-4 px-6 text-sm font-medium text-gray-400">Migration ID</th>
                <th className="text-left py-4 px-6 text-sm font-medium text-gray-400">Type</th>
                <th className="text-left py-4 px-6 text-sm font-medium text-gray-400">Source</th>
                <th className="text-left py-4 px-6 text-sm font-medium text-gray-400">Destination</th>
                <th className="text-left py-4 px-6 text-sm font-medium text-gray-400">Records</th>
                <th className="text-left py-4 px-6 text-sm font-medium text-gray-400">Duration</th>
                <th className="text-left py-4 px-6 text-sm font-medium text-gray-400">Status</th>
                <th className="text-left py-4 px-6 text-sm font-medium text-gray-400">Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {migrations.map((migration) => (
                <tr
                  key={migration.id}
                  className="border-t border-gray-800 hover:bg-gray-800/30 transition-colors cursor-pointer"
                >
                  <td className="py-4 px-6 text-sm text-white font-mono">{migration.id}</td>
                  <td className="py-4 px-6 text-sm text-white">{migration.dbType}</td>
                  <td className="py-4 px-6 text-sm text-white">{migration.source}</td>
                  <td className="py-4 px-6 text-sm text-white">{migration.destination}</td>
                  <td className="py-4 px-6 text-sm text-white">{migration.records}</td>
                  <td className="py-4 px-6 text-sm text-white">{migration.duration}</td>
                  <td className="py-4 px-6">
                    <div className="flex flex-col gap-2">
                      <span
                        className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                          migration.status
                        )}`}
                      >
                        {migration.status}
                      </span>
                      {migration.status === 'In Progress' && migration.progress && (
                        <div className="w-full bg-gray-700 rounded-full h-1.5">
                          <div
                            className="bg-blue-500 h-1.5 rounded-full transition-all"
                            style={{ width: `${migration.progress}%` }}
                          />
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-6 text-sm text-gray-400">{migration.timestamp}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-400">Showing 1 to 4 of 4 migrations</p>
        <div className="flex gap-2">
          <button className="px-4 py-2 bg-[#141414] text-gray-400 border border-gray-800 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50" disabled>
            Previous
          </button>
          <button className="px-4 py-2 bg-[#141414] text-gray-400 border border-gray-800 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50" disabled>
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Migrations;